/**
 * /auto-ecoles/:dept/:villeSlug/:schoolId/gerer
 * Page RGPD : signalement / revendication / opposition.
 * Un annuaire CNIL-friendly doit permettre à un professionnel de :
 *  - signaler une erreur (libre, anonyme)
 *  - revendiquer sa fiche (SIRET + OTP email)
 *  - demander le retrait (droit d'opposition RGPD art. 21)
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle, Sparkles, Mail, Building2, Info, Home, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { API, SITE_NAME, CONTACT_EMAIL, useSeoMeta } from './_shared';

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-white/70 text-[12px] font-bold uppercase tracking-widest">{label}</span>
      <input {...props} className="mt-1 w-full h-11 px-3 rounded-xl bg-[#141416] border border-white/10 text-white text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20" />
    </label>
  );
}
function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-white/70 text-[12px] font-bold uppercase tracking-widest">{label}</span>
      <textarea {...props} className="mt-1 w-full min-h-[110px] p-3 rounded-xl bg-[#141416] border border-white/10 text-white text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20" />
    </label>
  );
}

export default function AutoEcoleGerer() {
  const { dept, villeSlug, schoolId } = useParams();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('signaler');

  useSeoMeta({
    title: `Gérer ma fiche auto-école · ${SITE_NAME}`,
    description: 'Modifier, revendiquer ou demander le retrait d\'une fiche auto-école conformément à vos droits RGPD.',
    canonicalPath: `/auto-ecoles/${dept}/${villeSlug}/${schoolId}/gerer`,
  });

  useEffect(() => {
    fetch(`${API}/api/annuaire/school/${schoolId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setSchool(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [schoolId]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0b] text-white/50 p-8 text-sm">Chargement…</div>;
  if (!school) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white p-8">
        <p className="text-white/60">Fiche introuvable ou déjà retirée.</p>
        <Link to="/auto-ecoles" className="mt-4 inline-flex items-center gap-2 text-rose-300 underline">← Retour</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-16" data-testid="auto-ecole-gerer">
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/auto-ecoles" className="hover:text-white">Annuaire</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/auto-ecoles/${dept}/${villeSlug}`} className="hover:text-white truncate max-w-[160px]">{school.ville}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">Gérer ma fiche</span>
        </div>
      </div>

      <header className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/auto-ecoles/${dept}/${villeSlug}`} className="inline-flex items-center gap-1 text-white/50 hover:text-white text-[12px] mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la liste
        </Link>
        <div className="rounded-2xl bg-[#141416] border border-white/10 p-5">
          <p className="text-white/50 text-[11px] font-black uppercase tracking-widest">Fiche annuaire</p>
          <h1 className="text-2xl sm:text-3xl font-black mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{school.nom}</h1>
          <p className="text-white/60 text-sm mt-1">{school.adresse} · {school.code_postal} {school.ville}</p>
          {school.claim_status === 'verifiee' && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-blue-300 text-[11.5px] font-bold"><ShieldCheck className="w-3.5 h-3.5" /> Cette fiche est déjà revendiquée par son gérant.</p>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 bg-[#141416] border border-white/10 rounded-xl h-auto p-1">
            <TabsTrigger value="signaler" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-white/60 text-[11px] font-black uppercase tracking-widest py-2" data-testid="tab-signaler">Signaler une erreur</TabsTrigger>
            <TabsTrigger value="revendiquer" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-white/60 text-[11px] font-black uppercase tracking-widest py-2" data-testid="tab-revendiquer">Revendiquer</TabsTrigger>
            <TabsTrigger value="opposition" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-white/60 text-[11px] font-black uppercase tracking-widest py-2" data-testid="tab-opposition">Droit d'opposition</TabsTrigger>
          </TabsList>

          <TabsContent value="signaler" className="mt-6">
            <SignalerTab schoolId={schoolId} />
          </TabsContent>
          <TabsContent value="revendiquer" className="mt-6">
            <RevendiquerTab schoolId={schoolId} />
          </TabsContent>
          <TabsContent value="opposition" className="mt-6">
            <OppositionTab schoolId={schoolId} school={school} />
          </TabsContent>
        </Tabs>

        {/* Bas de page RGPD */}
        <div className="mt-10 pt-6 border-t border-white/5 text-white/40 text-[11.5px] leading-relaxed">
          <p>
            <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
            Les données affichées sur cette fiche sont issues de sources publiques officielles (Sécurité Routière · data.gouv.fr). Aucune donnée personnelle du dirigeant n'est publiée. Vos droits RGPD peuvent être exercés à tout moment : <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-rose-300">{CONTACT_EMAIL}</a> · <Link to="/mentions-annuaire" className="underline hover:text-rose-300">Mentions légales</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}

function SignalerTab({ schoolId }) {
  const [form, setForm] = useState({ field: 'phone', message: '', reporter_email: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 5) { toast.error('Merci de détailler l\'erreur (minimum 5 caractères).'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/annuaire/school/${schoolId}/report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('fail');
      setDone(true); toast.success('Signalement envoyé — merci.');
    } catch { toast.error('Erreur lors de l\'envoi.'); }
    setSending(false);
  };
  if (done) {
    return (
      <div className="rounded-2xl bg-emerald-950/30 border border-emerald-500/30 p-6 text-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
        <p className="text-white font-bold">Merci pour votre signalement.</p>
        <p className="text-white/60 text-[13px] mt-1">Notre équipe le traitera dans les prochains jours.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-2xl bg-[#141416] border border-white/10 p-5 space-y-4" data-testid="signaler-form">
      <div className="flex items-start gap-3 text-[12.5px] text-white/70 bg-white/[0.03] border border-white/10 rounded-xl p-3">
        <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <p>Signalez une donnée incorrecte (adresse, téléphone, site…). Le signalement est libre, gratuit, sans compte requis.</p>
      </div>
      <label className="block">
        <span className="text-white/70 text-[12px] font-bold uppercase tracking-widest">Champ concerné</span>
        <select value={form.field} onChange={e => setForm({ ...form, field: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-xl bg-[#141416] border border-white/10 text-white text-sm focus:outline-none focus:border-rose-500/50">
          <option value="phone">Téléphone</option>
          <option value="address">Adresse</option>
          <option value="website">Site internet</option>
          <option value="permis">Permis dispensés</option>
          <option value="other">Autre / fermeture</option>
        </select>
      </label>
      <TextArea label="Message" placeholder="Décrivez l'erreur ou fournissez la donnée correcte…" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="signaler-message" />
      <Field label="Votre email (optionnel)" type="email" placeholder="pour être recontacté si besoin" value={form.reporter_email} onChange={e => setForm({ ...form, reporter_email: e.target.value })} data-testid="signaler-email" />
      <button type="submit" disabled={sending} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black uppercase tracking-widest text-[12px] transition" data-testid="signaler-submit">
        {sending ? 'Envoi…' : 'Envoyer le signalement'}
      </button>
    </form>
  );
}

function RevendiquerTab({ schoolId }) {
  const [step, setStep] = useState('start'); // start | otp | done
  const [form, setForm] = useState({ siret: '', email: '', contact_name: '' });
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);

  const submitStart = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const res = await fetch(`${API}/api/annuaire/school/${schoolId}/claim/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const j = await res.json();
      if (!res.ok) { toast.error(j.detail || 'Erreur'); setSending(false); return; }
      toast.success('Code envoyé par email.'); setStep('otp');
    } catch { toast.error('Erreur réseau'); }
    setSending(false);
  };
  const submitOtp = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const res = await fetch(`${API}/api/annuaire/school/${schoolId}/claim/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, otp }),
      });
      const j = await res.json();
      if (!res.ok) { toast.error(j.detail || 'Code incorrect'); setSending(false); return; }
      toast.success('Fiche revendiquée !'); setStep('done');
    } catch { toast.error('Erreur réseau'); }
    setSending(false);
  };

  if (step === 'done') {
    return (
      <div className="rounded-2xl bg-emerald-950/30 border border-emerald-500/30 p-6 text-center">
        <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
        <p className="text-white font-black text-lg">Fiche revendiquée !</p>
        <p className="text-white/60 text-[13px] mt-2 max-w-md mx-auto">
          Votre fiche affiche maintenant le badge « Vérifiée ». Vous pouvez enrichir votre présentation (description, téléphone pro, site) en nous contactant à <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-rose-300">{CONTACT_EMAIL}</a>.
        </p>
      </div>
    );
  }

  if (step === 'otp') {
    return (
      <form onSubmit={submitOtp} className="rounded-2xl bg-[#141416] border border-white/10 p-5 space-y-4" data-testid="revendiquer-otp-form">
        <div className="flex items-start gap-3 text-[12.5px] text-white/70 bg-white/[0.03] border border-white/10 rounded-xl p-3">
          <Mail className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <p>Un code à 6 chiffres vient d'être envoyé à <strong className="text-white">{form.email}</strong>. Saisissez-le pour valider votre revendication.</p>
        </div>
        <Field label="Code de vérification (6 chiffres)" value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} inputMode="numeric" placeholder="000000" data-testid="revendiquer-otp" />
        <div className="flex gap-2">
          <button type="button" onClick={() => setStep('start')} className="h-11 px-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white text-[12px] font-bold transition">Retour</button>
          <button type="submit" disabled={sending || otp.length < 4} className="flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black uppercase tracking-widest text-[12px] transition" data-testid="revendiquer-otp-submit">
            {sending ? 'Vérification…' : 'Valider ma revendication'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitStart} className="rounded-2xl bg-[#141416] border border-white/10 p-5 space-y-4" data-testid="revendiquer-form">
      <div className="flex items-start gap-3 text-[12.5px] text-white/70 bg-blue-950/20 border border-blue-500/20 rounded-xl p-3">
        <Building2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <p><strong className="text-white">Vous êtes le gérant de cette auto-école ?</strong> Revendiquez gratuitement votre fiche pour la faire passer en « Vérifiée » et prendre le contrôle de vos informations.</p>
      </div>
      <Field label="SIRET (14 chiffres)" value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} maxLength={17} inputMode="numeric" placeholder="00000000000000" required data-testid="revendiquer-siret" />
      <Field label="Email professionnel" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="contact@votre-auto-ecole.fr" required data-testid="revendiquer-email" />
      <Field label="Votre nom (optionnel)" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} data-testid="revendiquer-name" />
      <button type="submit" disabled={sending} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black uppercase tracking-widest text-[12px] transition" data-testid="revendiquer-submit">
        {sending ? 'Envoi…' : 'Recevoir le code de vérification'}
      </button>
      <p className="text-white/40 text-[11px]">Un code à 6 chiffres sera envoyé à l'email indiqué. Aucun paiement, aucune obligation.</p>
    </form>
  );
}

function OppositionTab({ schoolId, school }) {
  const [form, setForm] = useState({ email: '', contact_name: '', reason: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.reason.length < 5) { toast.error('Merci d\'indiquer un motif (min. 5 caractères).'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/annuaire/school/${schoolId}/opposition/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const j = await res.json();
      if (!res.ok) { toast.error(j.detail || 'Erreur'); setSending(false); return; }
      setDone(true); toast.success('Demande enregistrée.');
    } catch { toast.error('Erreur réseau'); }
    setSending(false);
  };
  if (done) {
    return (
      <div className="rounded-2xl bg-amber-950/30 border border-amber-500/30 p-6 text-center">
        <Mail className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <p className="text-white font-bold">Demande enregistrée.</p>
        <p className="text-white/60 text-[13px] mt-2 max-w-md mx-auto">
          Un email de confirmation a été envoyé à <strong className="text-white">{form.email}</strong>. Cliquez sur le lien qu'il contient pour finaliser le retrait dans les 72 h. La fiche est déjà masquée à titre conservatoire.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-2xl bg-[#141416] border border-white/10 p-5 space-y-4" data-testid="opposition-form">
      <div className="flex items-start gap-3 text-[12.5px] text-white/70 bg-amber-950/20 border border-amber-500/20 rounded-xl p-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <p><strong className="text-white">Droit d'opposition (RGPD art. 21)</strong> : vous pouvez demander le retrait définitif de votre établissement de l'annuaire. La fiche sera masquée immédiatement, puis retirée après confirmation par email (72 h).</p>
      </div>
      <Field label="Email professionnel" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="opposition-email" />
      <Field label="Votre nom" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Prénom Nom" required data-testid="opposition-name" />
      <TextArea label="Motif de la demande" placeholder="Ex : établissement fermé, données inexactes, souhait de ne plus figurer…" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required data-testid="opposition-reason" />
      <button type="submit" disabled={sending} className="w-full h-11 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-black uppercase tracking-widest text-[12px] transition" data-testid="opposition-submit">
        {sending ? 'Envoi…' : 'Demander le retrait de ma fiche'}
      </button>
      <p className="text-white/40 text-[11px]">Gratuit, sans engagement. Aucun paiement n'est jamais demandé pour retirer une fiche.</p>
    </form>
  );
}
